import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PUSH_NAVIGATE_MESSAGE, PushPlatform } from './push-platform';

/**
 * - `unsupported`: this browser cannot receive push at all
 * - `needs-install`: iPhone/iPad in a browser tab; push needs the home-screen app
 * - `unavailable`: push is not configured on the server (no VAPID keys yet)
 * - `denied`: the user blocked notifications for Bildigsnetz
 * - `disabled` / `enabled`: this device is (not) registered
 */
export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'unavailable'
  | 'denied'
  | 'disabled'
  | 'enabled'
  | 'error';

const LOGOUT_UNREGISTER_TIMEOUT_MS = 3000;

interface PushConfig {
  enabled: boolean;
  public_key?: string | null;
}

interface PreparedPush {
  registration: ServiceWorkerRegistration;
  publicKey: string;
}

/**
 * Registers this device for Web Push (see backend `/v1/push`). Delivery and the
 * icon badge for a closed app are handled by `src/push-sw.js`.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platform = inject(PushPlatform);
  private readonly apiBase = environment.apiBaseUrl;

  private readonly _state = signal<PushState>(this.initialState());
  readonly state = this._state.asReadonly();
  private readonly _busy = signal(false);
  readonly busy = this._busy.asReadonly();

  private listeningForClicks = false;
  private syncing?: Promise<void>;
  private preparing?: Promise<PreparedPush | null>;

  /**
   * Loads the server key and registers the service worker ahead of the click,
   * so enable() can subscribe right after the permission prompt: Safari only
   * accepts a subscription close to the user's tap. Safe to call repeatedly.
   */
  prepare(): Promise<PreparedPush | null> {
    this.preparing ??= this.load().catch(e => {
      this.preparing = undefined;
      throw e;
    });
    return this.preparing;
  }

  private async load(): Promise<PreparedPush | null> {
    if (!this.canUsePush()) return null;
    const config = await this.getConfig();
    if (!config.enabled || !config.public_key) {
      if (this._state() === 'disabled') this._state.set('unavailable');
      return null;
    }
    const registration = await this.platform.registerServiceWorker();
    this.listenForNotificationClicks();
    return { registration, publicKey: config.public_key };
  }

  /**
   * After login: keep an already granted subscription registered for the
   * current user (endpoints can rotate, and a shared device may have switched
   * user). Never prompts. Runs once per page load.
   */
  syncExisting(): Promise<void> {
    this.syncing ??= this.sync();
    return this.syncing;
  }

  private async sync(): Promise<void> {
    if (!this.canUsePush() || this.platform.permission() !== 'granted') {
      this._state.set(this.initialState());
      return;
    }
    try {
      const prepared = await this.prepare();
      if (!prepared) {
        this._state.set('unavailable');
        return;
      }
      const { registration, publicKey } = prepared;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        this._state.set('disabled');
        return;
      }
      const current = this.hasKey(subscription, publicKey)
        ? subscription
        : await this.resubscribe(registration, subscription, publicKey);
      await this.saveSubscription(current);
      this._state.set('enabled');
    } catch (e) {
      console.warn('[push] sync failed', e);
      this._state.set('error');
    }
  }

  /** Must run from a user gesture: browsers only show the prompt then. */
  async enable(): Promise<void> {
    if (!this.canUsePush()) {
      this._state.set(this.initialState());
      return;
    }
    this._busy.set(true);
    try {
      const prepared = this.prepare();
      prepared.catch(() => undefined);
      const permission = await this.platform.requestPermission();
      if (permission !== 'granted') {
        this._state.set(permission === 'denied' ? 'denied' : 'disabled');
        return;
      }
      const ready = await prepared;
      if (!ready) {
        this._state.set('unavailable');
        return;
      }
      const { registration, publicKey } = ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing && this.hasKey(existing, publicKey)
          ? existing
          : await this.resubscribe(registration, existing, publicKey);
      await this.saveSubscription(subscription);
      this._state.set('enabled');
    } catch (e) {
      console.warn('[push] enable failed', e);
      this._state.set('error');
    } finally {
      this._busy.set(false);
    }
  }

  async disable(): Promise<void> {
    this._busy.set(true);
    try {
      await this.unregister();
      this._state.set(this.initialState());
    } catch (e) {
      console.warn('[push] disable failed', e);
      this._state.set('error');
    } finally {
      this._busy.set(false);
    }
  }

  /**
   * Before sign-out, while the token is still valid: the next person using
   * this device must not get the previous user's notifications.
   */
  async unregisterForLogout(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>(resolve => {
      timer = setTimeout(resolve, LOGOUT_UNREGISTER_TIMEOUT_MS);
    });
    try {
      await Promise.race([this.unregister(), timeout]);
    } catch (e) {
      console.warn('[push] unregister on logout failed', e);
    } finally {
      clearTimeout(timer);
    }
  }

  private async unregister(): Promise<void> {
    if (!this.canUsePush() || this.platform.permission() !== 'granted') return;
    const registration = await this.platform.registerServiceWorker();
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    try {
      await firstValueFrom(
        this.http.delete(`${this.apiBase}/v1/push/subscriptions`, {
          body: { endpoint },
        })
      );
    } catch (e) {
      console.warn(
        '[push] server unregister failed; it expires on next send',
        e
      );
    }
  }

  private initialState(): PushState {
    if (!this.platform.isPushSupported()) {
      return this.platform.isIos() && !this.platform.isStandalone()
        ? 'needs-install'
        : 'unsupported';
    }
    if (this.platform.isIos() && !this.platform.isStandalone()) {
      return 'needs-install';
    }
    const permission = this.platform.permission();
    if (permission === 'denied') return 'denied';
    return 'disabled';
  }

  private canUsePush(): boolean {
    const state = this.initialState();
    return state !== 'unsupported' && state !== 'needs-install';
  }

  private getConfig(): Promise<PushConfig> {
    return firstValueFrom(
      this.http.get<PushConfig>(`${this.apiBase}/v1/push/config`)
    );
  }

  private async resubscribe(
    registration: ServiceWorkerRegistration,
    existing: PushSubscription | null,
    publicKey: string
  ): Promise<PushSubscription> {
    if (existing) {
      await existing.unsubscribe();
    }
    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    });
  }

  private async saveSubscription(
    subscription: PushSubscription
  ): Promise<void> {
    const json = subscription.toJSON();
    await firstValueFrom(
      this.http.put(`${this.apiBase}/v1/push/subscriptions`, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys?.['p256dh'], auth: json.keys?.['auth'] },
      })
    );
  }

  private hasKey(subscription: PushSubscription, publicKey: string): boolean {
    const current = subscription.options?.applicationServerKey;
    if (!current) return false;
    const a = new Uint8Array(current);
    const b = base64UrlToBytes(publicKey);
    return a.length === b.length && a.every((value, i) => value === b[i]);
  }

  private listenForNotificationClicks(): void {
    if (this.listeningForClicks) return;
    this.listeningForClicks = true;
    this.platform.onServiceWorkerMessage(data => {
      const message = data as { type?: string; url?: string } | null;
      if (
        message?.type === PUSH_NAVIGATE_MESSAGE &&
        typeof message.url === 'string' &&
        message.url.startsWith('/') &&
        !message.url.startsWith('//')
      ) {
        void this.router.navigateByUrl(message.url);
      }
    });
  }
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}
